import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_BASE = `${BACKEND_URL}/api`;

const recentErrors = new Map();

function valueMessage(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === "string") return item;
      const field = Array.isArray(item?.loc) ? item.loc.filter((part) => part !== "body").join(" → ") : "";
      return `${field ? `${field}: ` : ""}${item?.msg || item?.message || JSON.stringify(item)}`;
    }).join("; ");
  }
  if (typeof value === "object") {
    return value.message || value.detail || value.error_description || value.bse_remarks ||
      value.response_string || value.error?.message || JSON.stringify(value);
  }
  return String(value);
}

export function getApiErrorMessage(error) {
  error.__smifsErrorHandled = true;
  if (!error?.response) {
    if (!error?.isAxiosError && error?.message) return error.message;
    return error?.code === "ECONNABORTED"
      ? "The server took too long to respond. The request may still be processing; check its status before retrying."
      : "The server could not be reached. Check your connection and try again. Do not repeat a money transaction until its status is confirmed.";
  }
  const { status, data } = error.response;
  const detail = valueMessage(data?.detail) || valueMessage(data?.error) || valueMessage(data?.message);
  if (detail) return detail;
  const defaults = {
    400: "The request was rejected. Review the entered details and try again.",
    401: "Your session has expired. Sign in again to continue.",
    403: "Your account is not permitted to perform this action.",
    404: "The requested record was not found or is no longer available.",
    409: "This action conflicts with the record's current exchange status. Refresh it before retrying.",
    422: "Some fields are invalid or incomplete. Review the form and try again.",
    429: "Too many requests were sent. Wait briefly before trying again.",
    500: "The server could not complete the request. The operation has not been assumed successful.",
    502: "BSE or an upstream service rejected the request. Review the exchange message and current status.",
    503: "The exchange service is temporarily unavailable. Check status before retrying.",
  };
  return defaults[status] || `The request failed with HTTP ${status}.`;
}

function showUnhandledError(error) {
  const message = getApiErrorMessage(error);
  error.__smifsErrorHandled = false;
  const path = error?.config?.url || "request";
  const key = `${path}:${message}`;
  const now = Date.now();
  if ((recentErrors.get(key) || 0) > now - 2500) return;
  recentErrors.set(key, now);
  toast.error(message, { duration: 7000 });
}

function createApi(tokenKey) {
  const instance = axios.create({ baseURL: API_BASE, timeout: 45_000 });
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem(tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Give page-level handlers one microtask to provide a more specific toast. Any
      // previously-silent request failure is surfaced here with a real server message.
      setTimeout(() => {
        if (!error.__smifsErrorHandled && error?.config?.showErrorToast !== false) showUnhandledError(error);
      }, 0);
      return Promise.reject(error);
    },
  );
  return instance;
}

export const CLIENT_TOKEN_KEY = "smifs_client_token";
export const ADMIN_TOKEN_KEY = "smifs_admin_token";
export const AIF_PROVIDER_TOKEN_KEY = "smifs_aif_provider_token";

export const clientApi = createApi(CLIENT_TOKEN_KEY);
export const adminApi = createApi(ADMIN_TOKEN_KEY);
export const aifProviderApi = createApi(AIF_PROVIDER_TOKEN_KEY);
